import { useCallback, useState } from "react";
import { useAuth } from "../contexts/AuthProvider";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";

type UseContractSectionsFilterArgs = {
	getStored: (departmentId: string, userId: string) => string[] | null;
	setStored: (departmentId: string, sections: string[], userId: string) => void;
	analyticsEvent: string;
};

type UseContractSectionsFilterResult = {
	selectedContractSections: Set<string>;
	handleContractSectionsChange: (sections: string[]) => void;
};

/**
 * Shared Contract Type filter state for staffing views (Weekly Gaps, Daily Standard,
 * Daily Gaps). Hydrates from localStorage on mount, persists changes per user+department,
 * and tracks an analytics event on every change.
 */
export function useContractSectionsFilter({ getStored, setStored, analyticsEvent }: UseContractSectionsFilterArgs): UseContractSectionsFilterResult {
	const { chosenDepartment } = useHospitalData();
	const { userInfo } = useAuth();
	const { track } = useAnalytics();
	const departmentId = chosenDepartment.department_id;
	const userId = userInfo?.id;

	const [selectedContractSections, setSelectedContractSections] = useState<Set<string>>(() => new Set(userId ? (getStored(departmentId, userId) ?? []) : []));

	const handleContractSectionsChange = useCallback(
		(sectionTitles: string[]) => {
			setSelectedContractSections(new Set(sectionTitles));
			if (userId) {
				setStored(departmentId, sectionTitles, userId);
			}
			track(analyticsEvent, { sections: JSON.stringify(sectionTitles) });
		},
		[departmentId, userId, track, setStored, analyticsEvent],
	);

	return { selectedContractSections, handleContractSectionsChange };
}
